import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Skeleton from "@mui/material/Skeleton"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Typography from "@mui/material/Typography"
import { useSelector } from "react-redux"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import {
  HeaderTableCell,
  NoWrapTableCell,
} from "../../../common/components/tables/cells"
import { isConcierge } from "../../users/accountRole"
import { selectProfile } from "../../users/reducer"
import BorderedGrayChip from "../components/BorderedGrayChip"
import ConfirmConnectEmailDialog, {
  useConnectEmailDialogRequirements,
  useEmailProviders,
} from "../components/ConfirmConnectEmailDialog"
import {
  selectAuthorizedEmailProvider,
  selectEmailAuthorizationLoading,
} from "../reducer"
import ConciergeEmailSignatureSection from "./ConciergeEmailSignatureSection"
import ConciergeZohoMailboxCard from "./ConciergeZohoMailboxCard"
import EmailSignatureSection from "./EmailSignatureSection"
import ProfileColumn from "./ProfileColumn"

export default function ConfigureEmailTab({ onStatusMessage }) {
  const profile = useSelector(selectProfile)
  const emailAuthorizationLoading = useSelector(selectEmailAuthorizationLoading)
  const {
    confirmConnectEmailDialogOpen,
    openConfirmConnectEmailDialog,
    handleConfirmConnectGmailDialogClosed,
    updateProviderConnection,
  } = useConnectEmailDialogRequirements()
  const authorizedEmailProvider = useSelector(selectAuthorizedEmailProvider)
  const providers = useEmailProviders()
  const concierge = isConcierge(profile)

  function handleConnectButtonClicked(provider) {
    if (authorizedEmailProvider) {
      updateProviderConnection(provider, false)
    } else {
      openConfirmConnectEmailDialog()
    }
  }

  return (
    <ProfileColumn>
      <Column sx={{ gap: 3, width: "100%" }}>
        {concierge ? (
          <>
            <ConciergeZohoMailboxCard onStatusMessage={onStatusMessage} />
            <ConciergeEmailSignatureSection onStatusMessage={onStatusMessage} />
          </>
        ) : (
          <>
            <Column sx={{ gap: 1 }}>
              <Typography variant="h6" fontWeight={700}>
                Email provider
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Connect Gmail or Microsoft so you can send templates and one-off
                messages from your account.
              </Typography>
            </Column>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <HeaderTableCell>Provider</HeaderTableCell>
                    <HeaderTableCell>Status</HeaderTableCell>
                    <HeaderTableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {emailAuthorizationLoading
                    ? providers.map((provider) => (
                        <TableRow key={provider.id}>
                          <NoWrapTableCell>
                            <Skeleton
                              variant="rectangular"
                              height={30}
                              width="100%"
                            />
                          </NoWrapTableCell>
                          <NoWrapTableCell>
                            <Skeleton
                              variant="rectangular"
                              height={30}
                              width={120}
                            />
                          </NoWrapTableCell>
                          <NoWrapTableCell>
                            <Skeleton
                              variant="rectangular"
                              height={30}
                              width={250}
                            />
                          </NoWrapTableCell>
                        </TableRow>
                      ))
                    : providers.map((provider) => (
                        <TableRow key={provider.id}>
                          <NoWrapTableCell
                            sx={{ textAlign: "center", width: "100%" }}
                          >
                            <Row
                              component="span"
                              sx={{ gap: 1, alignItems: "center" }}
                            >
                              <img
                                src={provider.icon}
                                alt="Gmail"
                                style={{ width: "32px" }}
                              />
                              <Typography
                                variant="body2"
                                component="span"
                                sx={{ fontWeight: "bold" }}
                              >
                                {provider.name}
                              </Typography>
                            </Row>
                          </NoWrapTableCell>
                          <NoWrapTableCell>
                            <BorderedGrayChip
                              label={
                                authorizedEmailProvider?.id === provider.id
                                  ? "Connected"
                                  : "Not connected"
                              }
                              sx={{ width: "120px" }}
                            />
                          </NoWrapTableCell>
                          <NoWrapTableCell>
                            <Button
                              variant="outlined"
                              color="inherit"
                              startIcon={<provider.muiIcon />}
                              onClick={() =>
                                handleConnectButtonClicked(provider)
                              }
                              sx={{ maxWidth: "300px", minWidth: "300px" }}
                            >
                              {authorizedEmailProvider?.id === provider.id
                                ? `Update ${provider.name} Connection`
                                : `Connect ${provider.name} Account`}
                            </Button>
                          </NoWrapTableCell>
                        </TableRow>
                      ))}
                </TableBody>
              </Table>
            </TableContainer>

            <EmailSignatureSection onStatusMessage={onStatusMessage} />

            <Alert severity="info">
              Company mailbox provisioning is available to Concierge users only.
            </Alert>
          </>
        )}
      </Column>

      {!concierge && (
        <ConfirmConnectEmailDialog
          open={confirmConnectEmailDialogOpen}
          onClose={handleConfirmConnectGmailDialogClosed}
        />
      )}
    </ProfileColumn>
  )
}
